import { Stack, Typography } from '@mui/material';
import React from 'react';
import { useNavigate } from 'react-router-dom';

import { NavigationConfig } from '../../utils/navigation-constants';
import { useTranslation } from '../../utils/translations';
import CustomButton from '../button/ButtonComponent';
import { AutoPayBadgeStyles, AutoPayOffBadgeStyles } from '../global-styles/GlobalStyles';

import type { ContractApiResponse } from '../../services/contract-services';

interface RowAutoPayActionProps {
    contract: ContractApiResponse | null | undefined;
    /** Smaller badge/button that can shrink, for single-row headers on narrow screens. */
    compact?: boolean;
}

/**
 * Per-contract autopay status for a make-payment row: a badge when autopay is active, otherwise
 * (primary contracts only) an "Enable Autopay" button that opens the contract details screen with
 * the autopay form already open.
 */
export const RowAutoPayAction: React.FC<RowAutoPayActionProps> = ({ contract, compact = false }) => {
    const { t } = useTranslation();
    const navigate = useNavigate();

    if (!contract) return null;

    const badgeSx = {
        width: 'fit-content',
        maxWidth: '100%',
        minWidth: 0,
        px: 1,
        ...(compact && { px: 0.75, py: '2px', flexShrink: 1, overflow: 'hidden', '&& span': { fontSize: 12 } }),
    };
    const badgeTextVariant = compact ? 'caption' : 'subtitle2';

    if (contract.AutoPay && !contract.AutopayInProgress) {
        return (
            <AutoPayBadgeStyles
                direction="row"
                alignItems="center"
                justifyContent="center"
                sx={{ ...badgeSx, gap: 0.5 }}
            >
                <span className="icon-check" />
                <Typography variant={badgeTextVariant} fontWeight={600} noWrap>
                    {t('contracts.details.autoPayEnabled')}
                </Typography>
            </AutoPayBadgeStyles>
        );
    }

    if (!contract.IsPrimary) {
        return (
            <AutoPayOffBadgeStyles direction="row" alignItems="center" justifyContent="center" sx={badgeSx}>
                <Typography variant={badgeTextVariant} fontWeight={600} noWrap>
                    {t('contracts.details.autoPayUnavailable')}
                </Typography>
            </AutoPayOffBadgeStyles>
        );
    }

    const inProgress = Boolean(contract.AutopayInProgress && !contract.AutoPay);

    return (
        <Stack
            onClick={e => e.stopPropagation()}
            sx={{
                position: 'relative',
                zIndex: 2,
                width: 'fit-content',
                maxWidth: '100%',
                minWidth: 0,
                pointerEvents: 'auto',
            }}
        >
            <CustomButton
                variant="outlined"
                fullWidth={false}
                SMButton
                sx={{ whiteSpace: 'nowrap' }}
                disabled={inProgress}
                disabledTooltip={inProgress ? t('contracts.details.enableAutopayInProgress') : ''}
                onClick={() =>
                    navigate(NavigationConfig.ContractDetails, { state: { contract, openAutoPayModal: true } })
                }
            >
                {t('contracts.details.enableAutoPay')}
            </CustomButton>
        </Stack>
    );
};
